/**
 * MetricsContext - Provides canonical metrics throughout the app
 *
 * This context serves as the single source of truth for all Bitcoin metrics.
 * Both the Dashboard components and AI Service consume metrics from here.
 *
 * Usage:
 *   // In component
 *   const { metrics, raw, formatted, progress } = useMetrics();
 *
 *   // In AI service
 *   import { useMetricsForAI } from '../contexts/MetricsContext';
 *   const { formattedForAI, preCalculated, summary } = useMetricsForAI();
 */

import { createContext, useContext, useMemo } from 'react';
import {
  calculateCanonicalMetrics,
  formatMetricsForAI,
  getPreCalculatedDataForAI,
  getMetricsSummary
} from '../utils/data/canonicalMetrics';
import { createCurrencyConverter } from '../utils/currencyConversion.utils';

// Create the context
const MetricsContext = createContext(null);

/**
 * MetricsProvider - Wraps the app and provides canonical metrics
 *
 * @param {Object} props.children - Child components
 * @param {Object} props.data - API data from dataOrchestrator
 * @param {number|string} props.bitcoinPrice - Current BTC price
 * @param {Object} props.lastMempoolBlockData - Latest block data
 * @param {Array} props.recentBlocks - Recent blocks array
 * @param {Object} props.fees - Fee recommendations
 * @param {Object} props.mempoolInfo - Mempool statistics
 * @param {Object} props.da - Difficulty adjustment data
 * @param {Object} props.diamondbackData - Diamondback API data
 * @param {Object} props.blockMetrics - Computed block metrics
 * @param {string} props.selectedCurrency - User's selected currency
 * @param {Array} props.blocks - Recent blocks from WebSocket
 */
export const MetricsProvider = ({
  children,
  data,
  bitcoinPrice,
  lastMempoolBlockData,
  recentBlocks,
  fees,
  mempoolInfo,
  da,
  diamondbackData,
  blockMetrics,
  selectedCurrency = 'usd',
  blocks
}) => {
  // Calculate conversion rates from data
  const conversionRates = useMemo(() => {
    if (!data) return {};
    return createCurrencyConverter(data) || {};
  }, [data]);

  // Calculate canonical metrics (memoized for performance)
  const metrics = useMemo(() => {
    return calculateCanonicalMetrics({
      data,
      bitcoinPrice,
      lastMempoolBlockData,
      recentBlocks,
      fees,
      mempoolInfo,
      da,
      diamondbackData,
      blockMetrics,
      selectedCurrency,
      conversionRates
    });
  }, [
    data,
    bitcoinPrice,
    lastMempoolBlockData,
    recentBlocks,
    fees,
    mempoolInfo,
    da,
    diamondbackData,
    blockMetrics,
    selectedCurrency,
    conversionRates
  ]);

  // Context value with all metrics and helpers
  const contextValue = useMemo(() => ({
    // The full metrics object
    metrics,

    // Shortcuts for common access patterns
    raw: metrics.raw,
    formatted: metrics.formatted,
    progress: metrics.progress,
    meta: metrics.meta,

    // Currency info
    selectedCurrency,
    conversionRates,

    // Raw data sources (for components that need direct access)
    sources: {
      data,
      bitcoinPrice,
      lastMempoolBlockData,
      recentBlocks,
      fees,
      mempoolInfo,
      da,
      diamondbackData,
      blockMetrics,
      blocks
    }
  }), [
    metrics,
    selectedCurrency,
    conversionRates,
    data,
    bitcoinPrice,
    lastMempoolBlockData,
    recentBlocks,
    fees,
    mempoolInfo,
    da,
    diamondbackData,
    blockMetrics,
    blocks
  ]);

  return (
    <MetricsContext.Provider value={contextValue}>
      {children}
    </MetricsContext.Provider>
  );
};

/**
 * useMetrics - Hook to access canonical metrics
 *
 * @returns {Object} Metrics context value
 * @example
 *   const { formatted, raw, progress } = useMetrics();
 *   console.log(formatted.bitcoinPrice); // "$97,234.56"
 *   console.log(raw.hashrate); // 1234567890000000000000
 */
export const useMetrics = () => {
  const context = useContext(MetricsContext);
  if (!context) {
    console.warn('useMetrics must be used within a MetricsProvider');
    return {
      metrics: null,
      raw: {},
      formatted: {},
      progress: {},
      meta: {},
      selectedCurrency: 'usd',
      conversionRates: {},
      sources: {}
    };
  }
  return context;
};

/**
 * useMetricsForAI - Hook specifically for AI service consumption
 *
 * @param {Array} selectedCategories - Optional categories to filter metrics
 * @returns {Object} AI-ready metrics
 * @example
 *   const { formattedForAI, preCalculated, summary } = useMetricsForAI(['market', 'network']);
 */
export const useMetricsForAI = (selectedCategories = null) => {
  const context = useContext(MetricsContext);

  return useMemo(() => {
    if (!context?.metrics) {
      return {
        formattedForAI: '',
        preCalculated: null,
        summary: null,
        metrics: null,
        isReady: false
      };
    }

    return {
      // Text format for AI prompts
      formattedForAI: formatMetricsForAI(context.metrics, selectedCategories),

      // Pre-calculated values AI should use verbatim
      preCalculated: getPreCalculatedDataForAI(context.metrics),

      // Summary object for mini dashboard
      summary: getMetricsSummary(context.metrics),

      // Full metrics object
      metrics: context.metrics,

      // Flag indicating data is ready
      isReady: context.meta?.hasPrice || context.meta?.hasLastBlock
    };
  }, [context, selectedCategories]);
};

/**
 * getMetricsForAI - Standalone function for use outside React components
 * Used by aiService.js when called from non-React contexts
 *
 * @param {Object} sources - All data sources
 * @param {Array} selectedCategories - Optional categories to filter
 * @returns {Object} AI-ready metrics
 */
export const getMetricsForAI = (sources, selectedCategories = null) => {
  const conversionRates = sources.data ? createCurrencyConverter(sources.data) || {} : {};

  const metrics = calculateCanonicalMetrics({
    ...sources,
    conversionRates
  });

  return {
    formattedForAI: formatMetricsForAI(metrics, selectedCategories),
    preCalculated: getPreCalculatedDataForAI(metrics),
    summary: getMetricsSummary(metrics),
    metrics
  };
};

export default MetricsContext;
